// ============================================================================
// The market vocabulary, READ from the database — never written down here.
//
// Before 2026-09-10 the eleven metros were written down in four places: a CHECK
// constraint on the building table, the body of the DB canonicaliser, the
// constants in `types.ts`, and a stale six-key parallel vocabulary with its own
// loose substring matcher in `design-tokens.ts`. Adding a market meant a
// migration, an app deploy, and remembering the fourth place existed. The
// onboarding card carried no constraint at all, which is how nineteen cards
// came to hold values the building table would refuse.
//
// Now `public.markets` is the one home and this module is the only door to it.
// Adding a market is a ROW. Nothing here needs to change.
//
// Shape copied deliberately from `use-can-access-sourcing.ts` rather than
// invented: module-level cache + a single inflight promise + listeners, so the
// account list, the facility card, the market stripe, the create modal and the
// card drawer share ONE read.
//
// ⛔ Two rules that module learned the hard way and this one inherits:
//   * never resolve before the bridged JWT arrives — the table is readable by
//     `authenticated`, so an early read executes as anon and returns nothing;
//   * NEVER cache an error or an empty answer. A market list that failed to
//     load must re-ask on the next mount, not pin "there are no markets" for
//     the session.
//
// ⛔ And the reason there is no hardcoded fallback list: a fallback is a second
// home, and a second home is the defect this module exists to remove. When the
// load fails, callers render the neutral colour and surface `error` — an
// unknown is a real answer and it gets named, not papered over.
// ============================================================================

import { useEffect, useState } from 'react';
import { getSupabaseToken, supabase } from './supabase';
import { token } from './design-tokens';

export interface Market {
  /** The canonical name, which IS the key everywhere it is stored. */
  market_name: string;
  /** The coarser operating grouping. Texas splits North / South. */
  region_name: string;
  /** Two-letter state codes a building in this market may sit in. */
  covers_states: string[];
  /** The market dot and stripe colour. */
  dot_color: string;
  /** Dark-page override, set only where the base colour is unreadable there. */
  dot_color_dark: string | null;
  is_active: boolean;
  sort_order: number;
}

/**
 * The neutral colour a market with no answer renders as.
 *
 * ⛔ Read from the token, never written down. A market's colour is data, but
 * "we don't know this market" is CHROME, and chrome belongs to tcs.css — which
 * is also what makes it follow the theme. A hex here would be the fifth home
 * for a colour in a run whose whole point was getting down to one.
 */
export function neutralMarketColor(): string {
  return token('--tcs-border');
}

let cached: Market[] | null = null;
let inflight: Promise<Market[]> | null = null;
const listeners = new Set<(v: Market[]) => void>();

async function waitForBridgedToken(maxMs = 15000): Promise<boolean> {
  const start = Date.now();
  while (!getSupabaseToken()) {
    if (Date.now() - start > maxMs) return false;
    await new Promise((r) => setTimeout(r, 250));
  }
  return true;
}

async function resolve(): Promise<Market[]> {
  if (cached !== null) return cached;
  if (!inflight) {
    inflight = (async () => {
      let result: Market[] = [];
      try {
        const authed = await waitForBridgedToken();
        if (authed) {
          const { data, error } = await supabase
            .from('markets')
            .select(
              'market_name, region_name, covers_states, dot_color, dot_color_dark, is_active, sort_order',
            )
            .order('sort_order');
          // Only a real, non-empty answer is cacheable. An error or an empty
          // table leaves `cached` null so the next mount re-asks.
          if (!error && data && data.length > 0) {
            cached = data as Market[];
            result = cached;
          }
        }
      } catch {
        /* leave cached null — retry on next mount */
      }
      inflight = null;
      const answer = cached ?? result;
      for (const fn of listeners) fn(answer);
      return answer;
    })();
  }
  return inflight;
}

/** Test / sign-out hook: drop the cached vocabulary so the next mount re-asks. */
export function resetMarketsCache(): void {
  cached = null;
}

/**
 * The loaded vocabulary. `markets` is empty until the first read resolves and
 * stays empty if it failed — callers must handle that rather than assume.
 */
export function useMarkets(): {
  markets: Market[];
  loading: boolean;
  error: boolean;
} {
  const [markets, setMarkets] = useState<Market[]>(cached ?? []);
  const [loading, setLoading] = useState(cached === null);

  useEffect(() => {
    if (cached !== null) {
      setMarkets(cached);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const listener = (v: Market[]) => {
      if (!cancelled) {
        setMarkets(v);
        setLoading(false);
      }
    };
    listeners.add(listener);
    void resolve();
    return () => {
      cancelled = true;
      listeners.delete(listener);
    };
  }, []);

  return { markets, loading, error: !loading && markets.length === 0 };
}

/** Active market names in display order — the options for every market picker. */
export function marketOptions(markets: Market[]): string[] {
  return markets.filter((m) => m.is_active).map((m) => m.market_name);
}

/**
 * The colour for a market name.
 *
 * ⛔ No substring matching. The vocabulary this replaced tested for "colorado"
 * and so painted Colorado Springs with Denver's colour, while seven of the
 * eleven metros — including all four Texas markets — matched nothing and fell
 * through to neutral. An exact key or the neutral colour; nothing in between.
 */
export function marketColor(
  markets: Market[],
  marketName: string | null | undefined,
): string {
  if (!marketName) return neutralMarketColor();
  return (
    markets.find((x) => x.market_name === marketName)?.dot_color ??
    neutralMarketColor()
  );
}

/**
 * Both colours for a market — the base and the dark-page value.
 *
 * ⛔ The caller hands BOTH to CSS as custom properties and lets the stylesheet
 * choose, rather than asking JS which theme is on. Two reasons, and the second
 * is the one that bites: `<ForceLight>` pins a subtree to the light channels
 * through CSS inheritance alone, so a component that resolved the theme in JS
 * would paint its dark value inside a light e-sign page; and tcs.css remains
 * the mechanism that decides, which is where the decision belongs.
 *
 * Markets are CATEGORICAL, not status — the colour IS the identity, so it stays
 * saturated in dark. Only a market whose base colour is unreadable on a dark
 * page carries an override, which today is the one near-black identity colour.
 */
export function marketColorPair(
  markets: Market[],
  marketName: string | null | undefined,
): { base: string; dark: string } {
  if (!marketName) {
    return { base: neutralMarketColor(), dark: neutralMarketColor() };
  }
  const m = markets.find((x) => x.market_name === marketName);
  if (!m) return { base: neutralMarketColor(), dark: neutralMarketColor() };
  return { base: m.dot_color, dark: m.dot_color_dark ?? m.dot_color };
}

/** The states a market covers. Empty when the market is unknown. */
export function marketCoversStates(
  markets: Market[],
  marketName: string | null | undefined,
): string[] {
  if (!marketName) return [];
  return markets.find((x) => x.market_name === marketName)?.covers_states ?? [];
}

/**
 * Whether a market covers a state. Used to warn BEFORE a save that the DB
 * guard would refuse — the same rule, not a second one. Unknown either side is
 * not a contradiction, so it answers true and lets the DB have the last word.
 */
export function marketCoversState(
  markets: Market[],
  marketName: string | null | undefined,
  state: string | null | undefined,
): boolean {
  if (!marketName || !state) return true;
  const covers = marketCoversStates(markets, marketName);
  if (covers.length === 0) return true;
  return covers.includes(state.trim().toUpperCase());
}
